import type { CabinetTranscription, FileCabinetFileUrls } from './file-cabinet.types';

/**
 * The two loaders a call's player is given (`PhoneDetails`): the recording's link, then its transcript.
 *
 * - Neither throws. `PhoneDetails` awaits both inside one `try/finally` without a `catch`, so a failed
 *   transcript would also lose the audio; each failure is reported through `note` instead.
 * - Nothing is asked once `live()` is false. `PhoneDetails` asks for the transcript after the recording
 *   whatever the recording answered, so after the drawer or page closed — its requests cancelled — it
 *   would still start one, and that request would switch the page's fetch group (and its error-dialog
 *   skip) back on for the next page. A late answer is ignored too.
 */
export function fileCabinetCallLoaders(options: {
    live: () => boolean;
    urls: Promise<FileCabinetFileUrls | undefined>;
    transcription: (documentId: string) => Promise<CabinetTranscription | undefined>;
    note: (what: 'recording' | 'transcript', outcome: 'failed' | 'none') => void;
}): {
    downloadDocument: () => Promise<{ url: string } | undefined>;
    transcribeDocument: (documentId: string) => Promise<CabinetTranscription | undefined>;
} {
    const { live, urls, transcription, note } = options;
    return {
        downloadDocument: async () => {
            try {
                const found = await urls;
                if (!live()) {
                    return undefined;
                }
                if (!found) {
                    note('recording', 'none');
                }
                return found ? { url: found.download } : undefined;
            } catch {
                if (live()) {
                    note('recording', 'failed');
                }
                return undefined;
            }
        },
        transcribeDocument: async documentId => {
            if (!live()) {
                return undefined;
            }
            try {
                const transcript = await transcription(documentId);
                if (!live()) {
                    return undefined;
                }
                if (!transcript?.transcription?.length) {
                    note('transcript', 'none');
                }
                return transcript;
            } catch {
                if (live()) {
                    note('transcript', 'failed');
                }
                return undefined;
            }
        },
    };
}
